import { OpportunityAndRiskCard } from '@/components/molecules/cards/opportunityAndRisk';
import { StyledCardContent, StyledCardTitle, StyledCardValue } from './styles';
import { Mobile } from '@/components/molecules/viewport/mobile';
import { IconButton } from '@/components/atoms/Button';
import { InfoOutlined } from '@mui/icons-material';
import { Text } from '@/components/atoms/text';
import type { DimensionVariable } from '@/graphql/types/dimensions';
import { useState, useEffect, useRef } from 'react';
import { Box, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { CustomTooltip } from '@/components/molecules/customTooltip';
import { HistoricalInformation } from '@/components/molecules/historicalInformation';

interface GenericCompositionProps {
  tableData: {
    opportunityVariables: DimensionVariable[];
    riskVariables: DimensionVariable[];
  };
  setOpenModal: (open: boolean) => void;
  setModalData: (data: DimensionVariable) => void;
  variant: 'opportunity' | 'risk';
}

export const GenericComposition = ({
  tableData,
  setOpenModal,
  setModalData,
  variant,
}: GenericCompositionProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const containerRef = useRef<HTMLDivElement>(null);

  const [openTooltipIndex, setOpenTooltipIndex] = useState<number | null>(null);

  const handleOpen = (index: number) => {
    setOpenTooltipIndex(openTooltipIndex === index ? null : index);
  };

  const handleClose = () => {
    setOpenTooltipIndex(null);
  };

  // Fechar tooltip ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        openTooltipIndex !== null
      ) {
        handleClose();
      }
    };

    if (openTooltipIndex !== null) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openTooltipIndex]);

  const sortVariablesByName = (variables: DimensionVariable[] = []): DimensionVariable[] => {
    return [...variables].sort((a, b) =>
      a.name.localeCompare(b.name, ['pt-BR', 'en-US'], { sensitivity: 'base' })
    );
  };

  return (
    <div ref={containerRef} style={{ width: '100%' }}>
      <OpportunityAndRiskCard
        variant={variant}
        empty={
          variant === 'opportunity'
            ? tableData.opportunityVariables.length === 0
            : tableData.riskVariables.length === 0
        }
        gap={0}
        padding={0}
      >
        {(variant === 'opportunity' ? tableData.opportunityVariables : tableData.riskVariables)
          ? sortVariablesByName(
              variant === 'opportunity' ? tableData.opportunityVariables : tableData.riskVariables
            ).map((row, index) => (
              <Box
                key={index}
                onClick={() => (isMobile ? undefined : handleOpen(index))}
                sx={{ cursor: 'pointer', width: '100%' }}
              >
                <CustomTooltip
                  open={openTooltipIndex === index}
                  onClose={handleClose}
                  content={
                    <HistoricalInformation
                      variableInfo={{
                        title: row.name,
                        description: row.description,
                        source: row.source,
                        period: row.period,
                      }}
                      timeSeries={row.time_series}
                    />
                  }
                >
                  <StyledCardContent variant={variant}>
                    <StyledCardTitle>
                      <Mobile>
                        <IconButton
                          onClick={() => {
                            setOpenModal(true);
                            setModalData(row);
                          }}
                          sx={{
                            padding: theme.spacing(0, 1),
                          }}
                        >
                          <InfoOutlined color="action" />
                        </IconButton>
                      </Mobile>

                      <Text variant="body2" color="text.primary">
                        {row.name}
                      </Text>
                    </StyledCardTitle>

                    <StyledCardValue>
                      <Text variant="subtitle2" color="text.primary">
                        {row.formatted_value}
                      </Text>
                    </StyledCardValue>
                  </StyledCardContent>
                </CustomTooltip>
              </Box>
            ))
          : null}
      </OpportunityAndRiskCard>
    </div>
  );
};
